// Generic form fields declared in schema (wizard steps, crud-table action inputs).
import { useState } from 'react';
import { Icon } from './Icon';

export interface FieldDef {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'date' | 'select' | 'multiselect' | 'month' | 'json' | 'textarea';
  required?: boolean;
  placeholder?: string;
  pattern?: string;
  options?: (string | { value: string; label: string })[];
  optionsFrom?: string;
  default?: unknown;
  /** Show only when other fields have these values, e.g. { "authMode": "client_credentials" }. */
  showIf?: Record<string, unknown>;
  help?: string;
}

export type Options = Record<string, { value: string; label: string }[]>;

function optionsFor(f: FieldDef, from: Options | undefined) {
  const raw = f.options ?? (f.optionsFrom ? from?.[f.optionsFrom] : []) ?? [];
  return raw.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

export function isVisible(f: FieldDef, values: Record<string, any>): boolean {
  if (!f.showIf) return true;
  return Object.entries(f.showIf).every(([k, v]) => (Array.isArray(v) ? v.includes(values[k]) : values[k] === v));
}

export function visibleFields(fields: FieldDef[], values: Record<string, any>): FieldDef[] {
  return fields.filter((f) => isVisible(f, values));
}

/** Only the values of fields currently shown are submitted (hidden credentials never leave the form). */
export function visibleValues(fields: FieldDef[], values: Record<string, any>): Record<string, any> {
  const shown = new Set(visibleFields(fields, values).map((f) => f.name));
  const known = new Set(fields.map((f) => f.name));
  return Object.fromEntries(Object.entries(values).filter(([k]) => shown.has(k) || !known.has(k)));
}

export function validateFields(fields: FieldDef[], values: Record<string, any>): string | null {
  for (const f of visibleFields(fields, values)) {
    const v = values[f.name];
    if (f.required && (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0))) return `${f.label} is required`;
    if (f.pattern && v && !new RegExp(f.pattern).test(String(v))) return `${f.label} has an invalid format`;
    if (f.type === 'email' && v && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v))) return `${f.label} must be an email address`;
    if (f.type === 'json' && v) {
      try {
        JSON.parse(v);
      } catch (e: any) {
        return `${f.label}: ${e.message}`;
      }
    }
  }
  return null;
}

/** Text shown for a value on review screens (secrets are masked). */
export function displayValue(f: FieldDef, v: any, options?: Options): string {
  if (f.type === 'password') return v ? '••••••••' : '—';
  const opts = optionsFor(f, options);
  const find = (x: string) => opts.find((o) => o.value === x)?.label ?? x;
  return Array.isArray(v) ? v.map(find).join(', ') || '—' : v !== undefined && v !== null && v !== '' ? find(String(v)) : '—';
}

function Label({ f }: { f: FieldDef }) {
  return (
    <span>
      {f.label}
      {f.required && ' *'}
    </span>
  );
}

function Help({ f }: { f: FieldDef }) {
  return f.help ? <span className="hint">{f.help}</span> : null;
}

function Secret({ f, value, onChange, id }: { f: FieldDef; value: any; onChange: (v: any) => void; id: string }) {
  const [shown, setShown] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        id={id}
        className="input"
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        spellCheck={false}
        placeholder={f.placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ flex: 1, fontFamily: shown ? 'var(--font-mono, monospace)' : undefined }}
      />
      <button type="button" className="icon-btn" aria-label={shown ? `Hide ${f.label}` : `Show ${f.label}`} title={shown ? 'Hide' : 'Show'} onClick={() => setShown((s) => !s)}>
        <Icon name={shown ? 'eyeOff' : 'eye'} size={15} />
      </button>
    </div>
  );
}

export function Field({ f, value, onChange, options }: { f: FieldDef; value: any; onChange: (v: any) => void; options?: Options }) {
  const id = `f-${f.name}`;
  if (f.type === 'select') {
    return (
      <label className="field" htmlFor={id}>
        <Label f={f} />
        <select id={id} className="select" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {optionsFor(f, options).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <Help f={f} />
      </label>
    );
  }
  if (f.type === 'multiselect') {
    const cur: string[] = Array.isArray(value) ? value : [];
    const opts = optionsFor(f, options);
    return (
      <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
        <Label f={f} />
        <div className="check-row">
          {opts.length === 0 && <span className="muted">None available</span>}
          {opts.map((o) => (
            <label key={o.value}>
              <input type="checkbox" checked={cur.includes(o.value)} onChange={(e) => onChange(e.target.checked ? [...cur, o.value] : cur.filter((x) => x !== o.value))} />
              {o.label}
            </label>
          ))}
        </div>
        <Help f={f} />
      </fieldset>
    );
  }
  if (f.type === 'json' || f.type === 'textarea') {
    return (
      <label className="field" htmlFor={id}>
        <Label f={f} />
        <textarea id={id} className="input" rows={f.type === 'json' ? 18 : 4} spellCheck={false} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
        <Help f={f} />
      </label>
    );
  }
  if (f.type === 'password') {
    return (
      <label className="field" htmlFor={id}>
        <Label f={f} />
        <Secret f={f} value={value} onChange={onChange} id={id} />
        <Help f={f} />
      </label>
    );
  }
  const inputType = f.type === 'number' ? 'number' : f.type === 'month' ? 'month' : f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : 'text';
  return (
    <label className="field" htmlFor={id}>
      <Label f={f} />
      <input
        id={id}
        className="input"
        type={inputType}
        placeholder={f.placeholder}
        autoComplete="off"
        spellCheck={false}
        value={value ?? ''}
        onChange={(e) => onChange(f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
      />
      <Help f={f} />
    </label>
  );
}
